<template>
  <!--
    «Реквизиты для оплаты» / «Место и время передачи» of a bank or cash top-up (docs/cabinet-redesign/02 §5.1 В-3): the
    amount to pay with our fee in it, the manager's requisites line by line with a copy button each, the deadline, and
    «Я отправил». A pointer: the text is the request's current one; a later release replaces this card.
  -->
  <div class="cb" :class="{ 'cb--muted': state?.superseded }" data-testid="chat-block-TOPUP_PAY_IN">
    <div class="cb__title" data-testid="topup-payin-title">
      {{ t(cash ? 'cabinet.topupChat.payInTitleCash' : 'cabinet.topupChat.payInTitle') }}
    </div>
    <div v-if="state?.superseded" class="cb__state" data-testid="chat-block-state">{{ t('vitrine.concierge.payin.superseded') }}</div>
    <p v-else-if="!r || !r.payInInstructions" class="cb__text" data-testid="topup-payin-closed">
      {{ t('cabinet.topupChat.payInClosed') }}
    </p>
    <template v-else>
      <div class="cb__row cb__summary" data-testid="topup-payin-ask">
        <span>{{ t(cash ? 'cabinet.topupChat.toPayCash' : 'cabinet.topupChat.toPay') }}</span>
        <strong v-if="toPay" class="num">
          {{ f.toPay(toPay.amount, toPay.currency) }} {{ toPay.currency }}
          <small v-if="fee" class="cb__rowSub" data-testid="topup-payin-fee">
            {{ fee.percent != null
              ? t('cabinet.topupChat.feeIncludedPct', { amount: f.money(fee.amount, fee.currency) + ' ' + fee.currency, percent: f.pct(fee.percent) })
              : t('cabinet.topupChat.feeIncluded', { amount: f.money(fee.amount, fee.currency) + ' ' + fee.currency }) }}
          </small>
        </strong>
        <span v-else class="cb__soft">{{ t('cabinet.topupChat.askInText') }}</span>
      </div>
      <div v-if="r.payment" class="cb__row" data-testid="topup-payin-to-card">
        <small>{{ t('cabinet.topupChat.toCard') }}</small>
        <span class="num">{{ f.money(r.payment.toCard, r.payment.currency) }} {{ r.payment.currency }}</span>
      </div>
      <div v-if="r.payment?.cardPrice" class="cb__row">
        <small>{{ t('vitrine.topup.feeIssuance') }}</small>
        <span class="num">{{ f.money(r.payment.cardPrice, r.payment.currency) }} {{ r.payment.currency }}</span>
      </div>
      <div v-if="r.fundingDeadline" class="cb__row" data-testid="topup-payin-deadline">
        <small>{{ t(cash ? 'cabinet.topupChat.deadlineCash' : 'cabinet.topupChat.deadline') }}</small>
        <span>{{ f.dateTime(r.fundingDeadline) }}</span>
      </div>

      <div class="cb__detail" data-testid="topup-payin">
        <div class="cb__detailHead">
          <small>{{ t(cash ? 'cabinet.topupChat.whereCash' : 'cabinet.topupChat.requisites') }}</small>
          <button type="button" class="cb__copy" data-testid="topup-payin-copy-all" @click="copy(r.payInInstructions, -1)">
            <IconCopy :size="14" />{{ copiedAt === -1 ? t('common.copied') : t('cabinet.topupChat.copyAll') }}
          </button>
        </div>
        <ul class="tp-lines">
          <li v-for="(l, i) in lines" :key="i" class="tp-lines__line" :data-testid="'topup-payin-line'">
            <span class="tp-lines__text">
              <small v-if="l.label">{{ l.label }}</small>
              <span :class="{ num: !!l.label }">{{ l.value }}</span>
            </span>
            <button v-if="l.label" type="button" class="cb__copy" :aria-label="t('vitrine.topup.payin.copy') + ' ' + l.label"
                    :data-testid="'topup-payin-copy'" @click="copy(l.value, i)">
              <IconCopy :size="14" /><span v-if="copiedAt === i">{{ t('common.copied') }}</span>
            </button>
          </li>
        </ul>
      </div>

      <div v-if="r.status === 'NEEDS_INFO' || r.needsInfoReason" class="cb__notice" data-testid="topup-payin-hold">
        <div class="cb__noticeTitle">{{ t('cabinet.topupChat.hold') }}</div>
      </div>
      <button v-if="!r.markedSent" type="button" class="cb__primary" :disabled="disabled"
              data-testid="topup-mark-sent" @click="markSent">{{ t(forMethod('vitrine.topup.iSent', r.sourceKind)) }}</button>
      <p v-else class="cb__hint" data-testid="topup-marked-sent">{{ t(forMethod('vitrine.topup.sentAck', r.sourceKind)) }}</p>
    </template>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconCopy } from '@/components/ui/icons';
import { markTopUpSent } from '@/api/topup';
import type { BlockState } from '@/api/support';
import { forMethod } from '@/views/vitrine/topupStatus';
import { amountToPay, feeOf, requisiteLines } from '@/views/cabinet/concierge/topUpChat';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'TOPUP_PAY_IN' }>;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const f = useCardFormat();
const { running, error, run } = useCardAction(() => props.reread());

const r = computed(() => (props.state && !props.state.superseded ? props.state.request : null));
const cash = computed(() => props.state?.request?.sourceKind === 'CASH' || r.value?.sourceKind === 'CASH');
const toPay = computed(() => (r.value ? amountToPay(r.value) : null));
const fee = computed(() => (r.value ? feeOf(r.value) : null));
const lines = computed(() => requisiteLines(r.value?.payInInstructions));
const disabled = computed(() => !!props.busy || running.value);

function markSent() {
  const id = r.value?.id;
  if (id) void run(() => markTopUpSent(id));
}

/** Which line was just copied (-1 — the whole text), for the «Скопировано» mark. */
const copiedAt = ref<number | null>(null);
async function copy(text: string | null | undefined, at: number) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copiedAt.value = at;
    setTimeout(() => { if (copiedAt.value === at) copiedAt.value = null; }, 2000);
  } catch { /* clipboard denied — the text is on screen and selectable anyway */ }
}
</script>

<style scoped lang="scss">
@use './cards.scss';

.tp-lines { list-style: none; margin: 6px 0 0; padding: 0; display: flex; flex-direction: column; }
.tp-lines__line {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 7px 0;
  & + & { border-top: 1px solid var(--line); }
}
.tp-lines__text {
  display: flex; flex-direction: column; gap: 1px; min-width: 0; font-size: 13px; color: var(--onyx);
  overflow-wrap: anywhere;
  small { font-size: 10.5px; color: var(--muted); }
}
</style>
